import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCallback } from 'react';
import debounce from './debounce';

const configuredBaseUrl = import.meta.env.VITE_API_BASE_URL?.trim();
export const BASE_URL = (configuredBaseUrl || '').replace(/\/+$/, '');

const configuredWebSocketUrl = import.meta.env.VITE_WS_URL?.trim();
const defaultWebSocketUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;
export const WEBSOCKET_URL = (configuredWebSocketUrl || defaultWebSocketUrl).replace(/\/+$/, '');

let lastVerificationTime = 0;
const VERIFICATION_COOLDOWN = 5000;
const PUBLIC_ROUTES = ['/', '/login', '/signup', '/help'];

export const configureAxios = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common['Authorization'];
  }
};

export const useTokenVerification = (token, setNotification) => {
  const navigate = useNavigate();
  const location = useLocation();

  const verifyAndRefreshToken = useCallback(debounce(async (force = false) => {
    const now = Date.now();
    if (!force && now - lastVerificationTime < VERIFICATION_COOLDOWN) return true;
    if (PUBLIC_ROUTES.includes(location.pathname)) return true;
    if (!token) {
      navigate('/login');
      return false;
    }

    try {
      const response = await axios.get(`${BASE_URL}/api/status`, { 
        timeout: 60000,
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200) {
        lastVerificationTime = now;
        return true;
      }
      return false;
    } catch (error) {
      if (error.code === 'ECONNABORTED') return true;
      if (error.response?.status === 401) {
        try {
          const refreshResponse = await axios.post(`${BASE_URL}/api/token/refresh`, {}, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 30000
          });
          if (refreshResponse.data?.access_token) {
            const newToken = refreshResponse.data.access_token;
            localStorage.setItem('token', newToken);
            configureAxios(newToken);
            setNotification({ type: 'success', message: 'Session refreshed' });
            lastVerificationTime = now;
            return true;
          }
        } catch {
          // Refresh failure is handled by clearing the session below.
        }
      }
      localStorage.removeItem('token');
      navigate('/login');
      setNotification({ type: 'error', message: 'Session expired.' });
      return false;
    }
  }, 1000), [token, navigate, setNotification, location.pathname]);

  return verifyAndRefreshToken;
};

export const notificationStyles = {
  success: { backgroundColor: '#d4edda', color: '#155724', borderColor: '#c3e6cb' },
  error: { backgroundColor: '#f8d7da', color: '#721c24', borderColor: '#f5c6cb' },
  warning: { backgroundColor: '#fff3cd', color: '#856404', borderColor: '#ffeeba' },
};

export const getWebSocketConfig = (token, setNotification) => ({
  url: `${WEBSOCKET_URL}?token=${encodeURIComponent(token)}`,
  options: {
    shouldReconnect: (closeEvent) => closeEvent.code !== 1008 && closeEvent.code !== 1005,
    reconnectAttempts: 10,
    reconnectInterval: (attempt) => Math.min(1000 * Math.pow(2, attempt), 30000),
    connectionTimeout: 5000, // Delay connection attempt
    heartbeat: {
      message: JSON.stringify({ type: "ping" }),
      interval: 30000,
    },
    onError: () => {
      setNotification({ type: 'error', message: 'WebSocket connection failed.' });
    },
    onMessage: (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'keepalive') {
          if (data.message === 'Ping') return { type: 'ping', message: 'pong' };
          if (data.message === 'Pong') return data;
        } else if (data.type === 'progress') {
          setNotification({ type: 'info', message: `Screener progress: ${data.progress}%`, timestamp: Date.now() });
          return data;
        } else if (data.type === 'error') {
          setNotification({ type: 'error', message: data.message, timestamp: Date.now() });
        }
        return data;
      } catch {
        return null;
      }
    },
    onOpen: () => setNotification({ type: 'success', message: 'WebSocket connected' }),
    onClose: (event) => {
      setNotification({ type: 'warning', message: `WebSocket disconnected (Code: ${event.code}, Reason: ${event.reason})` });
    },
  },
  handleConnectionState: (readyState) => {
    switch (readyState) {
      case 0: return 'connecting';
      case 1: return 'connected';
      case 2: return 'closing';
      case 3: return 'disconnected';
      default: return 'unknown';
    }
  },
});

axios.interceptors.request.use(
  (config) => {
    if (config.url.includes('/api/token') && config.method.toLowerCase() !== 'post') {
      throw new Error('Token endpoint requires POST');
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    return Promise.reject(error);
  }
);
